import { Switch } from '@thanx/uikit/switch'
import { useEffect, useState } from 'react'
import { I18n } from 'react-redux-i18n'

import { buildTranslate } from 'locales'
import ModifierOrProductInput from 'scenes/RedeemManager/Builder/ConfigurationStep/ModifierOrProductInput'
import ModifierToggle from 'scenes/RedeemManager/Builder/ConfigurationStep/ModifierToggle'

import { type Fields } from 'models/CouponPool'
import { type DiscountScope } from 'scenes/RedeemManager/Builder/FormModel'
import {
  getEffectiveDiscountScope,
  getFreeItemRestrictionNoteKey,
  getFreeItemsNoteKey,
  isFixedPriceKey,
  isModifierDiscountScope,
} from 'scenes/RedeemManager/helpers'
import { type RedeemTypeKeys } from 'scenes/RedeemManager/types'

type ConditionalProductInputProps = {
  couponPool: Fields | null
  displayRequiredProducts: boolean
  requiredProductIds: string[]
  isFree?: boolean
  displayDiscountedProducts: boolean
  discountedProductIds: string[]
  discountedModifierIds?: string[]
  modifierDiscountIds?: string[]
  modifierDiscountsAdvanced?: boolean
  onModifierDiscountsAdvancedChange?: (advanced: boolean) => void
  displayFreeModifiers?: boolean
  redeemType?: RedeemTypeKeys
  discountScope?: DiscountScope
}

const t = buildTranslate(
  'redeem_manager.builder.steps.configuration.instore_redemption_card.coupon_codes'
)
const wrapperT = buildTranslate('redeem_manager.modifier_select.wrapper')

const ConditionalProductInput = ({
  couponPool,
  displayRequiredProducts,
  requiredProductIds,
  isFree,
  displayDiscountedProducts,
  discountedProductIds,
  discountedModifierIds,
  modifierDiscountIds = [],
  modifierDiscountsAdvanced = false,
  onModifierDiscountsAdvancedChange,
  displayFreeModifiers = false,
  redeemType,
  discountScope,
}: ConditionalProductInputProps) => {
  const effectiveScope = getEffectiveDiscountScope(discountScope)
  const modifiersRequired = redeemType === 'any_modifier_for_$x'
  const popoverVariant =
    redeemType === 'get_a_free_item' ? 'freeItem' : 'default'
  const modifierScopeSelected =
    effectiveScope === 'required_modifiers' ||
    effectiveScope === 'base_item_plus_all_modifiers' ||
    effectiveScope === 'base_item_plus_required_modifiers'
  const isFixedPriceType = isFixedPriceKey(redeemType)
  const displayModifierDiscounts =
    isFixedPriceType && isModifierDiscountScope(discountScope)
  const setModifierDiscountsAdvanced = (advanced: boolean) =>
    onModifierDiscountsAdvancedChange?.(advanced)

  const freeRestrictionNoteKey = getFreeItemRestrictionNoteKey(
    redeemType,
    discountScope
  )
  const freeItemsNoteKey = getFreeItemsNoteKey(redeemType, discountScope)

  const getModifierLabel = () => {
    if (freeRestrictionNoteKey) return t('free_restriction_title')
    if (!isFixedPriceType) return t('discounted_free_item_modifier_title')

    switch (effectiveScope) {
      case 'base_item_plus_all_modifiers':
      case 'base_item_plus_required_modifiers':
      case 'required_modifiers':
        return t('discounted_fixed_price_modifier_title')
      default:
        return t('discounted_free_item_modifier_title')
    }
  }

  const getModifierSecondaryText = () => {
    if (freeRestrictionNoteKey) return t('free_restriction_help_text')
    if (!isFixedPriceType) return t('discounted_label_free_item_modifier')

    switch (effectiveScope) {
      case 'base_item':
        return t('discounted_label_modifier_base_item')
      case 'base_item_plus_all_modifiers':
        return t('discounted_label_modifier_all_modifiers')
      case 'base_item_plus_required_modifiers':
      case 'required_modifiers':
        return t('discounted_label_modifier_required_modifiers')
      default:
        return t('discounted_label_free_item_modifier')
    }
  }
  const hasDiscountedModifierIds = Boolean(
    Array.isArray(discountedModifierIds) && discountedModifierIds.length > 0
  )
  const [isModifierToggleVisible, setIsModifierToggleVisible] = useState(
    modifiersRequired || modifierScopeSelected || hasDiscountedModifierIds
  )

  useEffect(() => {
    if (
      modifierScopeSelected ||
      modifiersRequired ||
      hasDiscountedModifierIds
    ) {
      setIsModifierToggleVisible(true)
    }
  }, [modifierScopeSelected, modifiersRequired, hasDiscountedModifierIds])
  return (
    <>
      <Switch condition={Boolean(couponPool && displayRequiredProducts)}>
        <ModifierOrProductInput
          label={t('required_label_fallback')}
          name="instoreRequiredProductIds"
          className="mb-m"
          value={requiredProductIds}
          placeholder={I18n.t('redeem_manager.product_select.placeholder')}
          validations={{ isRequired: true }}
          validationErrors={{
            isRequired: I18n.t('validations.is_required'),
          }}
        />
      </Switch>
      <Switch condition={Boolean(couponPool && displayDiscountedProducts)}>
        <ModifierOrProductInput
          label={
            isFree
              ? t('discounted_free_item_title')
              : t('discounted_label_free_item')
          }
          secondaryText={isFree ? t('discounted_label_free_item') : undefined}
          name="instoreDiscountedProductIds"
          value={discountedProductIds}
          placeholder={I18n.t('redeem_manager.product_select.placeholder')}
          validations={{ isRequired: true }}
          validationErrors={{
            isRequired: I18n.t('validations.is_required'),
          }}
        />
        <p className="mt-xs body-text-4 grey-70">
          {freeItemsNoteKey
            ? t(freeItemsNoteKey)
            : isFree
            ? t('will_apply_lowest_free_item')
            : t('will_apply_lowest')}
        </p>
        <Switch condition={displayFreeModifiers && !isFixedPriceType}>
          <p className="mt-xs body-text-4 grey-70">{t('including_addons')}</p>
        </Switch>
        <Switch condition={Boolean(displayFreeModifiers)}>
          {modifiersRequired ? (
            <div className="mt-m">
              <ModifierOrProductInput
                label={getModifierLabel()}
                secondaryText={getModifierSecondaryText()}
                tertiaryText={t('discounted_free_item_modifier_help_text')}
                name="instoreDiscountedModifierIds"
                value={discountedModifierIds}
                shouldDisplayModifierPopover={true}
                popoverVariant={popoverVariant}
                shouldDisplayInstoreValidationNote={true}
              />
            </div>
          ) : displayModifierDiscounts ? (
            // Restrictions are always shown under modifier discounts; outside
            // advanced mode they double as the discounted modifiers
            <div className="mt-m">
              <ModifierOrProductInput
                label={t(
                  modifierDiscountsAdvanced
                    ? 'modifier_discounts_required_title'
                    : 'modifier_discounts_simple_title'
                )}
                secondaryText={t(
                  modifierDiscountsAdvanced
                    ? 'modifier_discounts_required_help_text'
                    : 'modifier_discounts_simple_help_text'
                )}
                name="instoreDiscountedModifierIds"
                value={discountedModifierIds}
                noteText={
                  modifierDiscountsAdvanced
                    ? undefined
                    : t('modifier_discounts_cheapest_note')
                }
                shouldDisplayModifierPopover={true}
                popoverVariant={popoverVariant}
                shouldDisplayInstoreValidationNote={true}
              />
            </div>
          ) : (
            <ModifierToggle
              isVisible={isModifierToggleVisible}
              setIsVisible={setIsModifierToggleVisible}
              contentId="instore-modifier-restrictions"
            >
              <div className="mt-m">
                <ModifierOrProductInput
                  label={getModifierLabel()}
                  secondaryText={getModifierSecondaryText()}
                  tertiaryText={
                    freeRestrictionNoteKey
                      ? undefined
                      : t('discounted_free_item_modifier_help_text')
                  }
                  name="instoreDiscountedModifierIds"
                  toggleVisibility={setIsModifierToggleVisible}
                  hideLabel={
                    freeRestrictionNoteKey
                      ? t('free_restriction_disable')
                      : undefined
                  }
                  value={discountedModifierIds}
                  noteText={
                    freeRestrictionNoteKey
                      ? t(freeRestrictionNoteKey)
                      : undefined
                  }
                  shouldDisplayModifierPopover={true}
                  popoverVariant={popoverVariant}
                  shouldDisplayInstoreValidationNote={true}
                />
              </div>
            </ModifierToggle>
          )}
        </Switch>
        <Switch condition={displayModifierDiscounts}>
          <ModifierToggle
            isVisible={modifierDiscountsAdvanced}
            contentId="instore-modifier-discounts"
            setIsVisible={setModifierDiscountsAdvanced}
            buttonLabel={wrapperT('display_modifier_discount_ids')}
          >
            <div className="mt-m">
              <ModifierOrProductInput
                label={t('modifier_discounts_title')}
                secondaryText={t('modifier_discounts_help_text')}
                name="instoreModifierDiscountIds"
                onHide={() => setModifierDiscountsAdvanced(false)}
                hideLabel={wrapperT('hide_modifier_discount_ids')}
                value={modifierDiscountIds}
                shouldDisplayInstoreValidationNote={true}
              />
            </div>
          </ModifierToggle>
        </Switch>
      </Switch>
    </>
  )
}

export default ConditionalProductInput
